import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import Footer from './Footer';
import { LegalContentsDisclosure } from './LegalContentsDisclosure';
import { scrollLegalHash } from '../navigation/goToSection';
import { setCanonical } from '../navigation/canonical';

export interface LegalSection {
  id: string;
  title: string;
  content: React.ReactNode;
}

type LegalPageLayoutProps = {
  path: '/privacy' | '/terms';
  pageTitle: string;
  badge: string;
  title: string;
  lead: string;
  dates: [string, string];
  intro: string;
  sections: LegalSection[];
  contact: { title: string; body: string; cta: string; href: string };
};

// Shared shell for the privacy and terms pages (WU-8): WU-1 tokens and type scale, a sticky contents
// column on desktop, white section cards, no curves. The legal text itself stays in each page.
const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  path,
  pageTitle,
  badge,
  title,
  lead,
  dates,
  intro,
  sections,
  contact,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    document.title = pageTitle;
    setCanonical(path);
    scrollLegalHash(location.hash);
  }, [location.hash, pageTitle, path]);

  return (
    <div className="min-h-screen">
      <Header />

      <main>
        <section className="legal-hero band-teal">
          <div className="legal-hero__inner">
            <p className="legal-hero__badge">{badge}</p>
            <h1 className="legal-hero__title">{title}</h1>
            <p className="legal-hero__lead">{lead}</p>
            <div className="legal-hero__dates">
              <span>{dates[0]}</span>
              <span className="hidden sm:inline" aria-hidden="true">•</span>
              <span>{dates[1]}</span>
            </div>
          </div>
        </section>

        <section className="legal-body">
          <div className="legal-body__inner">
            <aside className="legal-toc">
              <div className="legal-toc__panel">
                <p className="legal-toc__title">{t('contents')}</p>
                <nav aria-label={t('contents')}>
                  <ul className="legal-toc__list">
                    {sections.map((s) => (
                      <li key={s.id}>
                        <a href={`#${s.id}`} className="legal-toc__link">
                          {s.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </aside>

            <article className="legal-article">
              <LegalContentsDisclosure sections={sections} />
              <p className="privacy-body legal-intro">{intro}</p>

              {sections.map((section) => (
                <div key={section.id} id={section.id} className="legal-section">
                  <div className="legal-card">
                    <h2 className="privacy-heading">{section.title}</h2>
                    {section.content}
                  </div>
                </div>
              ))}

              <div className="legal-contact band-teal">
                <h3 className="legal-contact__title">{contact.title}</h3>
                <p className="legal-contact__body">{contact.body}</p>
                <a href={contact.href} className="btn legal-contact__cta">
                  {contact.cta}
                </a>
              </div>
            </article>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LegalPageLayout;
